export type EffectiveElementAccess = "read" | "read-write";

interface RuntimeElementAccess {
  id?: string | number;
  parent_element_id?: string | number | null;
  parentElementId?: string | number | null;
  effective_access?: EffectiveElementAccess | "none";
}

/** The runtime API omits denied elements; this also rejects any orphaned descendants. */
export function visibleRuntimeElements<T extends RuntimeElementAccess>(
  elements: T[],
): T[] {
  const byId = new Map(
    elements.map((element) => [String(element.id), element]),
  );
  const resolved = new Map<
    string,
    { visible: boolean; access?: EffectiveElementAccess }
  >();

  const resolve = (
    element: T,
    ancestors: Set<string>,
  ): { visible: boolean; access?: EffectiveElementAccess } => {
    const id = String(element.id);
    const cached = resolved.get(id);
    if (cached) return cached;
    if (element.effective_access === "none" || ancestors.has(id))
      return { visible: false };
    const parentId = element.parent_element_id ?? element.parentElementId;
    if (parentId === undefined || parentId === null) {
      const result = { visible: true, access: element.effective_access };
      resolved.set(id, result);
      return result;
    }
    const parent = byId.get(String(parentId));
    if (!parent) return { visible: false };
    const parentResult = resolve(parent, new Set(ancestors).add(id));
    const result = {
      visible: parentResult.visible,
      access:
        parentResult.access === "read"
          ? ("read" as const)
          : element.effective_access,
    };
    resolved.set(id, result);
    return result;
  };

  return elements.flatMap((element) => {
    const result = resolve(element, new Set());
    if (!result.visible) return [];
    return result.access === element.effective_access
      ? [element]
      : [{ ...element, effective_access: result.access }];
  });
}

export function isWritableAccess(
  access: EffectiveElementAccess | undefined,
): boolean {
  // Older builder responses have no access field. Runtime responses set it.
  return access !== "read";
}

/**
 * Whether a field's own configuration makes it display-only: `readonly`, `is_readonly` or the
 * camelCase `readOnly` set to true, or `displayMode` of "text" or "readOnly". A display-only
 * field is never part of the submitted form, so it gives no Cancel or Submit footer.
 */
export function isReadOnlyFieldConfig(
  config: Record<string, unknown> | null | undefined,
): boolean {
  if (!config) return false;
  return (
    config.readonly === true ||
    config.is_readonly === true ||
    config.readOnly === true ||
    config.displayMode === "text" ||
    config.displayMode === "readOnly"
  );
}
